import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors, pageHeaderStyles } from "../AgenticPaymentAirdrop/styles";

// Admin refund of a store payment (BC-2110). No OTP, no store approval.
// The refund leaves the store wallet; the payment fee goes back with it: the
// store's share from the store, BananaCrystal's part from the fee wallet.
// Styles are inline: the admin page loads this pack's JS but not its CSS.

const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
  display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto",
};
const modal = {
  background: colors.white, borderRadius: "10px", padding: "20px 24px", width: "100%", maxWidth: "860px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};
const th = {
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`,
};
const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
// IDs are long; wrap them so the table fits.
const idCell = { ...td, fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all", minWidth: "120px", maxWidth: "200px" };
// border-box so width: 100% includes the padding and fields don't run into each other;
// a fixed height because admin CSS styles text inputs but not number ones.
const input = { height: "38px", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" };
const fieldLabel = { display: "block", fontWeight: 600, marginBottom: "4px" };
const Required = () => <span style={{ color: "#dc3545" }} aria-hidden="true"> *</span>;
const btn = (bg, disabled) => ({
  background: disabled ? "#9aa0b4" : bg, color: "#fff", border: 0, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
});
const ghostBtn = {
  background: "#fff", color: colors.primary, border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: "pointer",
};
const notice = (ok) => ({
  padding: "10px 14px", borderRadius: "6px", marginBottom: "16px", fontSize: "13px",
  background: ok ? "#d4edda" : "#f8d7da", color: ok ? "#155724" : "#721c24",
});

const num = (v) => {
  const n = Number(v && typeof v === "object" ? v.amount : v);
  return Number.isFinite(n) ? n : 0;
};
const money = (v, currency) =>
  `${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })}${currency ? ` ${currency}` : ""}`;
// lp-api amounts have at most 6 decimals, so a sum fixed to 6 is exact.
const add6 = (...xs) => Number(xs.reduce((t, x) => t + num(x), 0).toFixed(6));
const EPSILON = 1e-9;
// Amounts are exact to 6 decimals; anything smaller than that is the same amount.
const TOLERANCE = 0.0000005;
const PAGE_SIZE = 25;

/**
 * The fee that goes back with a refund of `amount`, the same way lp-api works
 * it out: in proportion to the share of the payment refunded, and whatever is
 * left when the refund finishes the payment. Only completed refunds count as
 * already returned. lp-api's response has the exact numbers.
 */
export const feeParts = (state, amount) => {
  const settledTotal = num(state.settledPay);
  const feeTotal = Math.max(num(state.feeAmount), 0);
  if (feeTotal <= 0 || settledTotal <= 0) return { share: 0, fee: 0 };
  const shareTotal = Math.min(Math.max(num(state.storeShareAmount), 0), feeTotal);
  const platformTotal = feeTotal - shareTotal;
  const done = (state.refunds || []).filter((r) => r.status === "completed");
  const shareDone = done.reduce((t, r) => t + num(r.shareReturned), 0);
  const feeDone = done.reduce((t, r) => t + num(r.feeReturned), 0);
  const finishes = settledTotal - num(state.refundedPay) - amount <= EPSILON;
  const ratio = Math.min(amount / settledTotal, 1);
  const part = (total, already) => {
    const left = Math.max(total - already, 0);
    return Number((finishes ? left : Math.min(total * ratio, left)).toFixed(6));
  };
  return { share: part(shareTotal, shareDone), fee: part(platformTotal, feeDone) };
};

/** What the payer can still get back in total, fee included (settled currency). */
export const totalLeft = (state) => {
  if (state.refundableTotal != null) return num(state.refundableTotal);
  const remaining = Math.max(num(state.settledPay) - num(state.refundedPay), 0);
  const parts = feeParts(state, remaining);
  return remaining + parts.share + parts.fee;
};

/**
 * The settled amount behind a total the payer gets back, as lp-api works it
 * out: start from the proportional split, then adjust the settled part until
 * settled + share + fee is exactly the total. Asking for everything that's
 * left (within 0.005, like lp-api) refunds the rest of the payment.
 */
export const settledFromTotal = (state, total) => {
  const remaining = Math.max(num(state.settledPay) - num(state.refundedPay), 0);
  if (total >= totalLeft(state) - 0.005) return remaining;
  const settled = num(state.settledPay);
  const fee = Math.max(num(state.feeAmount), 0);
  let amount = Number(((total * settled) / (settled + fee)).toFixed(6));
  for (let i = 0; i < 3; i += 1) {
    const parts = feeParts(state, amount);
    const next = Number((total - parts.share - parts.fee).toFixed(6));
    if (Math.abs(next - amount) < EPSILON) break;
    amount = next;
  }
  return amount;
};

const Status = ({ value }) => {
  const c = { completed: ["#d4edda", "#155724"], settled: ["#d1ecf1", "#0c5460"], refunded: ["#e2e3e5", "#383d41"],
    pending: ["#fff3cd", "#856404"], failed: ["#f8d7da", "#721c24"], none: ["#f1f1f1", "#666"] }[value] || ["#f1f1f1", "#666"];
  return (
    <span style={{ background: c[0], color: c[1], borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" }}>
      {value || "-"}
    </span>
  );
};

const Row = ({ label, children }) => (
  <div style={{ display: "flex", gap: "12px", fontSize: "13px", padding: "4px 0" }}>
    <span style={{ width: "170px", color: colors.gray600 }}>{label}</span>
    <span>{children}</span>
  </div>
);

const StorePaymentRefunds = ({ pageUrl, paymentId: initialId, query: initialQuery }) => {
  const url = (action) => `${pageUrl.replace(/\/$/, "")}/${action}`;
  const [paymentId, setPaymentId] = useState(initialId || "");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(null); // { amount, reason, reference, confirming }
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [filters, setFilters] = useState({ q: initialQuery || "", status: "", from: "", to: "" });
  const [list, setList] = useState({ data: [], total: 0, offset: 0, loading: true, error: null });
  const [storeLabel, setStoreLabel] = useState(null);

  const closePayment = () => { setState(null); setForm(null); setMsg(null); setStoreLabel(null); };

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !saving) closePayment(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadList = useCallback(async (offset = 0, f = filters) => {
    setList((l) => ({ ...l, loading: true, error: null }));
    try {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      Object.entries(f).forEach(([k, v]) => { if (v.trim()) qs.set(k, v.trim()); });
      const res = await getRequest(`${url("store_payments")}?${qs.toString()}`);
      if (!res?.success) throw new Error(res?.error || "Could not load store payments.");
      setList({ data: res.data?.data || [], total: res.data?.total || 0, offset, loading: false, error: null });
    } catch (e) {
      setList((l) => ({ ...l, loading: false, error: e?.response?.data?.error || e.message || "Could not load store payments." }));
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (id) => {
    const target = (id ?? paymentId).trim();
    if (!target) return;
    setLoading(true);
    try {
      const res = await getRequest(`${url("payment_refunds")}?payment_id=${encodeURIComponent(target)}`);
      if (!res?.success) throw new Error(res?.error || "Could not load this payment.");
      setState(res.data);
      return res.data;
    } catch (e) {
      setState(null);
      setMsg({ ok: false, text: e?.response?.data?.error || e.message || "Could not load this payment." });
    } finally {
      setLoading(false);
    }
  }, [paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadList(0);
    if (initialId) load(initialId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open a payment from the list; with refund, go straight to the refund form.
  const openPayment = async (row, refund) => {
    setMsg(null);
    setForm(null);
    setPaymentId(row.id);
    setStoreLabel(row.storeName ? `${row.storeName}${row.storeUsername ? ` (@${row.storeUsername})` : ""}` : null);
    const data = await load(row.id);
    if (data && refund) setForm({ amount: String(Number(totalLeft(data).toFixed(6))), reason: "", reference: "", confirming: false });
  };

  const cur = state?.settledCurrency || "";
  // "Customer paid" can add the fee only when the customer paid in the settled currency.
  const sameCurrency = !!state && String(state.paidCurrency || "").toUpperCase() === cur.toUpperCase();
  // Everything here is what the customer gets back, fee included.
  const refundable = state ? totalLeft(state) : 0;
  const totalNum = form ? Number(form.amount) : 0;
  const amountError = !form ? null
    : form.amount.trim() === "" || !Number.isFinite(totalNum) || totalNum <= 0 ? "Enter an amount above 0."
      : !/^\d*(\.\d{0,6})?$/.test(form.amount.trim()) ? "Use up to 6 decimals."
        : totalNum - refundable > TOLERANCE ? `Up to ${money(refundable, cur)}.` : null;
  const amountNum = form && !amountError ? settledFromTotal(state, totalNum) : 0;
  const preview = form && !amountError ? feeParts(state, amountNum) : null;

  // Until there's a valid amount, the total is everything that's left.
  const shownSettled = preview ? amountNum : state ? settledFromTotal(state, refundable) : 0;
  const shownParts = preview || (state ? feeParts(state, shownSettled) : null);
  const fromStore = shownParts ? add6(shownSettled, shownParts.share) : 0;
  const fromUs = shownParts ? add6(shownParts.fee) : 0;
  const breakdown = shownParts && refundable > EPSILON && (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", margin: "0 0 6px" }}>
        <span style={{ fontSize: "13px", color: colors.gray600 }}>Total refund</span>
        <strong style={{ fontSize: "20px", color: "#9a3412" }}>{money(add6(fromStore, fromUs), cur)}</strong>
      </div>
      <ul style={{ fontSize: "13px", margin: "0 0 8px", paddingLeft: "18px" }}>
        <li>
          {money(fromStore, cur)} from the store wallet ({money(shownSettled)} payment
          {shownParts.share > 0 && <> + {money(shownParts.share)} of the store's fee share</>})
        </li>
        {fromUs > 0 && <li>{money(fromUs, cur)} service fee from BananaCrystal</li>}
      </ul>
    </>
  );

  const openForm = () => {
    setMsg(null);
    setForm({ amount: String(Number(refundable.toFixed(6))), reason: "", reference: "", confirming: false });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const full = totalNum >= refundable - TOLERANCE;
      const body = { payment_id: state.paymentId, description: form.reason.trim(), reference: form.reference.trim() };
      if (!full) body.amount_total = form.amount.trim();
      const res = await postRequest(url("refund_payment"), body);
      if (!res?.success) throw { data: res };
      const d = res.data || {};
      const feeNote = d.feeRefundStatus === "failed"
        ? " The service fee part failed; use Retry fee below."
        : "";
      setMsg({
        ok: true,
        text: (() => {
          const fromStore = add6(d.amountSettled ?? amountNum, d.shareReturned);
          const fromUs = add6(d.feeReturned);
          return `Refunded. The customer gets back ${money(add6(fromStore, fromUs), cur)}: ${money(fromStore, cur)} from the store wallet and ${money(fromUs, cur)} service fee from BananaCrystal.${feeNote}`;
        })(),
      });
      setForm(null);
      await load(state.paymentId);
      loadList(list.offset);
    } catch (e) {
      const err = e?.data?.error || e?.error || "The refund failed.";
      setMsg({ ok: false, text: err });
      setForm((f) => (f ? { ...f, confirming: false } : f));
      await load(state.paymentId);
    } finally {
      setSaving(false);
    }
  };

  const retryFee = async (refundId) => {
    setRetrying(refundId);
    try {
      const res = await postRequest(url("retry_refund_fee"), { refund_id: refundId });
      if (!res?.success) throw { data: res };
      setMsg({ ok: true, text: "Service fee sent again." });
    } catch (e) {
      setMsg({ ok: false, text: e?.data?.error || "Retry failed." });
    } finally {
      setRetrying(null);
      await load(state.paymentId);
      loadList(list.offset);
    }
  };

  return (
    <div>
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>Store Payments</h1>
      </div>
      {msg && !state && <div role="status" style={notice(msg.ok)}>{msg.text}</div>}

      <div style={card}>
        <form
          onSubmit={(e) => { e.preventDefault(); loadList(0); }}
          style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "12px" }}
        >
          <label style={{ fontSize: "12px", flex: "1 1 260px" }}>
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>Search</div>
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                   placeholder="Payment ID, tx ID, customer email or account, store name or @handle" style={{ ...input, width: "100%" }} />
          </label>
          <label style={{ fontSize: "12px" }}>
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>Status</div>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={input}>
              <option value="">Settled and refunded</option>
              <option value="settled">Settled</option>
              <option value="refunded">Refunded</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label style={{ fontSize: "12px" }}>
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>From</div>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={input} />
          </label>
          <label style={{ fontSize: "12px" }}>
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>To</div>
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={input} />
          </label>
          <button type="submit" style={btn(colors.primary, list.loading)} disabled={list.loading}>Search</button>
        </form>

        {list.error ? (
          <p style={{ color: "#721c24", fontSize: "13px" }}>{list.error}</p>
        ) : list.loading && list.data.length === 0 ? (
          <p style={{ color: colors.gray600, fontSize: "13px" }}>Loading…</p>
        ) : list.data.length === 0 ? (
          <p style={{ color: colors.gray600, fontSize: "13px" }}>No store payments match.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Date</th><th style={th}>Payment ID</th><th style={th}>Transaction ID</th><th style={th}>Store</th>
                  <th style={th}>Customer Email</th><th style={th}>Paid</th>
                  <th style={th}>Store received</th><th style={th}>Refunded</th><th style={th}>Status</th><th style={th} />
                </tr>
              </thead>
              <tbody>
                {list.data.map((p) => {
                  const canRefund = p.status === "settled" && num(p.refundableTotal ?? p.refundablePay) > EPSILON;
                  return (
                    <tr key={p.id} style={state?.paymentId === p.id ? { background: "#f5f7ff" } : undefined}>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{p.createdAt ? new Date(p.createdAt).toLocaleString() : "-"}</td>
                      <td style={idCell}>{p.id}</td>
                      <td style={idCell}>{p.txId || "-"}</td>
                      <td style={td}>
                        {p.storeName || "-"}
                        {p.storeUsername && <div style={{ fontSize: "11px", color: colors.gray600 }}>@{p.storeUsername}</div>}
                      </td>
                      {/* The account only when the customer isn't a BC user (no email). */}
                      <td style={{ ...td, fontSize: "12px" }} title={p.payerAccount || undefined}>{p.payerEmail || p.payerAccount || "-"}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{money(p.paidAmount, p.paidCurrency)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{money(p.settledPay, p.settledCurrency)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{num(p.refundedTotal ?? p.refundedPay) > 0 ? money(p.refundedTotal ?? p.refundedPay, p.settledCurrency) : "-"}</td>
                      <td style={td}><Status value={p.status} /></td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        <button type="button" style={{ ...ghostBtn, padding: "4px 10px", fontSize: "12px", marginRight: "6px" }} onClick={() => openPayment(p, false)}>
                          View
                        </button>
                        {canRefund && (
                          <button type="button" style={{ ...btn("#c0392b", false), padding: "4px 10px", fontSize: "12px" }} onClick={() => openPayment(p, true)}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {list.total > PAGE_SIZE && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "10px", fontSize: "12px" }}>
            <button type="button" style={ghostBtn} disabled={list.offset === 0 || list.loading} onClick={() => loadList(Math.max(list.offset - PAGE_SIZE, 0))}>Previous</button>
            <span>{list.offset + 1}-{Math.min(list.offset + PAGE_SIZE, list.total)} of {list.total}</span>
            <button type="button" style={ghostBtn} disabled={list.offset + PAGE_SIZE >= list.total || list.loading} onClick={() => loadList(list.offset + PAGE_SIZE)}>Next</button>
          </div>
        )}
      </div>

      {state && (
        <div style={overlay} onClick={closePayment} role="presentation">
        <div style={modal} role="dialog" aria-modal="true" aria-label={`Payment ${state.paymentId}`} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h2 style={{ margin: 0, fontSize: "18px" }}>Store payment</h2>
            <button type="button" onClick={closePayment} aria-label="Close" style={{ background: "none", border: 0, fontSize: "22px", cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
          {msg && <div role="status" style={notice(msg.ok)}>{msg.text}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <Row label="Payment ID">{state.paymentId} <Status value={state.paymentStatus} /></Row>
              <Row label="Store">{storeLabel || state.storeId || "-"}</Row>
              <Row label="Customer paid">
                {sameCurrency
                  ? <>{money(add6(state.paidAmount, state.feeAmount), cur)} ({money(state.paidAmount)} + {money(state.feeAmount)} service fee)</>
                  : money(state.paidAmount, state.paidCurrency)}
              </Row>
              <Row label="Store got">{money(state.settledPay, cur)}</Row>
              <Row label="Service fee">
                {money(state.feeAmount, cur)}
                {num(state.storeShareAmount) > 0 && <> ({money(state.storeShareAmount)} of it went to the store)</>}
              </Row>
              {/* refundedTotal: what the customer got back, fee included (older lp-api: settled part only). */}
              <Row label="Refunded so far">{money(state.refundedTotal ?? state.refundedPay, cur)}</Row>
              {/* Follows the amount as it's typed. */}
              {breakdown && <div style={{ marginTop: "12px" }}>{breakdown}</div>}
            </div>
            {!form && (
              <button type="button" style={btn("#c0392b", refundable <= EPSILON)} disabled={refundable <= EPSILON} onClick={openForm}>
                Refund
              </button>
            )}
          </div>

          {form && (
            <div style={{ marginTop: "16px", padding: "16px", background: "#fafafa", border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
              {!form.confirming ? (
                <>
                  <p style={{ fontSize: "12px", color: colors.gray600, margin: "0 0 10px" }}>
                    This refund goes out right away, with no OTP and no approval from the store. The service fee goes back to the customer too.
                  </p>
                  {/* autoComplete off: Chrome offered saved email addresses in the reason box. */}
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "14px 16px" }}>
                    <label style={{ fontSize: "13px" }}>
                      <span style={fieldLabel}>Amount the customer gets back ({cur}, up to {money(refundable)})<Required /></span>
                      <input type="number" min="0" step="any" max={refundable} value={form.amount} required aria-required="true"
                             autoComplete="off" name="refund_amount"
                             onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ ...input, width: "100%" }} />
                      {amountError && <span style={{ display: "block", color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{amountError}</span>}
                    </label>
                    <label style={{ fontSize: "13px" }}>
                      <span style={fieldLabel}>Reference (optional)</span>
                      <input value={form.reference} maxLength={100} autoComplete="off" name="refund_reference"
                             onChange={(e) => setForm({ ...form, reference: e.target.value })} style={{ ...input, width: "100%" }} />
                    </label>
                    <label style={{ fontSize: "13px", gridColumn: "1 / -1" }}>
                      <span style={fieldLabel}>Reason (we email this to the customer)<Required /></span>
                      <input value={form.reason} maxLength={500} required aria-required="true"
                             autoComplete="off" name="refund_reason"
                             onChange={(e) => setForm({ ...form, reason: e.target.value })} style={{ ...input, width: "100%" }} />
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "14px" }}>
                    <button type="button" style={ghostBtn} onClick={() => setForm(null)}>Cancel</button>
                    <button type="button" style={btn(colors.primary, !!amountError || !form.reason.trim())}
                            disabled={!!amountError || !form.reason.trim()} onClick={() => setForm({ ...form, confirming: true })}>
                      Review refund
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: "12px", color: colors.gray600, margin: "0 0 12px" }}>
                    {!sameCurrency && state.paidCurrency ? `The customer gets it in ${state.paidCurrency}. ` : ""}
                    Reason: {form.reason.trim()}. This can't be undone.
                  </p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button type="button" style={ghostBtn} onClick={() => setForm({ ...form, confirming: false })} disabled={saving}>Back</button>
                    <button type="button" style={btn("#c0392b", saving)} disabled={saving} onClick={submit}>
                      {saving ? "Refunding…" : "Confirm refund"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <h3 style={{ fontSize: "15px", margin: "24px 0 8px" }}>Refund history</h3>
          {(state.refunds || []).length === 0 ? (
            <p style={{ fontSize: "13px", color: colors.gray600 }}>No refunds yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>When</th><th style={th}>Amount</th><th style={th}>Status</th>
                    <th style={th}>Service fee</th><th style={th}>By</th><th style={th}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {state.refunds.map((r) => (
                    <tr key={r.id}>
                      <td style={td}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : "-"}</td>
                      <td style={td}>
                        {money(r.amountSettled, cur)}
                        {num(r.shareReturned) > 0 && <div style={{ fontSize: "11px", color: colors.gray600 }}>+ {money(r.shareReturned, cur)} fee share</div>}
                      </td>
                      <td style={td}>
                        <Status value={r.status} />
                        {r.failureReason && <div style={{ fontSize: "11px", color: "#721c24" }}>{r.failureReason}</div>}
                      </td>
                      <td style={td}>
                        {money(r.feeReturned, cur)} <Status value={r.feeRefundStatus} />
                        {r.feeRefundStatus === "failed" && (
                          <div style={{ marginTop: "4px" }}>
                            {r.feeRefundError && <div style={{ fontSize: "11px", color: "#721c24" }}>{r.feeRefundError}</div>}
                            <button type="button" style={{ ...ghostBtn, padding: "4px 10px", fontSize: "12px", marginTop: "4px" }}
                                    disabled={retrying === r.id} onClick={() => retryFee(r.id)}>
                              {retrying === r.id ? "Retrying…" : "Retry fee"}
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={td}>{r.refundedBy || "-"}</td>
                      <td style={td}>{r.description || "-"}{r.reference ? <div style={{ fontSize: "11px", color: colors.gray600 }}>Ref: {r.reference}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
};

export default StorePaymentRefunds;
